/**
 * Security Review Tab
 * 
 * Comprehensive security governance dashboard:
 * - High-risk plugins
 * - Quarantined plugins
 * - Disabled plugins
 * - Security statistics
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Popconfirm,
  message,
  Badge
} from 'antd';
import {
  SafetyOutlined,
  WarningOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  StopOutlined
} from '@ant-design/icons';
import DetailedPluginView from '../components/DetailedPluginView';

const { Title, Text } = Typography;

interface SecurityReviewData {
  high_risk_plugins: Array<{
    plugin_id: string;
    name: string;
    version: string;
    risk_score: number;
    high_risk_operations: number;
  }>;
  quarantined_plugins: Array<{
    plugin_id: string;
    name: string;
    version: string;
    reason: string;
  }>;
  disabled_plugins: Array<{
    plugin_id: string;
    name: string;
    version: string;
  }>;
  statistics: {
    total_plugins: number;
    high_risk_count: number;
    quarantined_count: number;
    disabled_count: number;
    enabled_count: number;
  };
}

const SecurityReviewTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SecurityReviewData | null>(null);
  const [selectedPluginId, setSelectedPluginId] = useState<string | null>(null);
  const [detailedViewVisible, setDetailedViewVisible] = useState(false);

  useEffect(() => {
    fetchSecurityReview();
  }, []);

  const fetchSecurityReview = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/plugins/security/review', {
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Failed to load security review: ${res.status}`);
      
      const result = await res.json();
      setData(result);
    } catch (error: any) {
      message.error(`Failed to load security review: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleQuarantine = async (pluginId: string) => {
    try {
      const res = await fetch(`/api/plugins/${pluginId}/quarantine`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) throw new Error(`Quarantine failed: ${res.status}`);
      
      const result = await res.json();
      message.success(result.message || `Plugin ${pluginId} quarantined`);
      await fetchSecurityReview();
    } catch (error: any) {
      message.error(`Quarantine failed: ${error.message}`);
    }
  };

  const handleViewDetails = (pluginId: string) => {
    setSelectedPluginId(pluginId);
    setDetailedViewVisible(true);
  };

  const highRiskColumns = [
    {
      title: 'Plugin',
      key: 'plugin',
      render: (_: any, record: any) => (
        <Space>
          <Text strong>{record.name}</Text>
          <Tag>{record.version}</Tag>
        </Space>
      )
    },
    {
      title: 'Risk Score',
      dataIndex: 'risk_score',
      key: 'risk_score',
      width: 120,
      render: (score: number) => (
        <Badge
          count={score}
          overflowCount={99}
          style={{ backgroundColor: '#ff4d4f' }}
        />
      ),
      sorter: (a: any, b: any) => a.risk_score - b.risk_score
    },
    {
      title: 'High-Risk Operations',
      dataIndex: 'high_risk_operations',
      key: 'high_risk_operations',
      width: 150
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record.plugin_id)}
          >
            Details
          </Button>
          <Popconfirm
            title="Quarantine this plugin?"
            description="This will disable the plugin and mark it for security review."
            onConfirm={() => handleQuarantine(record.plugin_id)}
            okText="Yes, Quarantine"
            cancelText="Cancel"
          >
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
            >
              Quarantine
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const quarantinedColumns = [
    {
      title: 'Plugin',
      key: 'plugin',
      render: (_: any, record: any) => (
        <Space>
          <Text strong>{record.name}</Text>
          <Tag>{record.version}</Tag>
        </Space>
      )
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: any) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record.plugin_id)}
        >
          View Details
        </Button>
      )
    }
  ];

  const disabledColumns = [
    {
      title: 'Plugin',
      key: 'plugin',
      render: (_: any, record: any) => (
        <Space>
          <Text strong>{record.name}</Text>
          <Tag>{record.version}</Tag>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: any) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record.plugin_id)}
        >
          View Details
        </Button>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Title level={3}>
          <SafetyOutlined /> Security Review
        </Title>
        <Text type="secondary">
          Monitor plugin security, manage high-risk plugins, and review quarantined plugins
        </Text>
      </div>

      {/* Statistics */}
      {data && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Plugins"
                value={data.statistics.total_plugins}
                prefix={<SafetyOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="High Risk"
                value={data.statistics.high_risk_count}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Quarantined"
                value={data.statistics.quarantined_count}
                prefix={<StopOutlined />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Disabled"
                value={data.statistics.disabled_count}
                prefix={<PauseCircleOutlined />}
                valueStyle={{ color: '#8c8c8c' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* High Risk Plugins */}
      <Card
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>High Risk Plugins</span>
            {data && (
              <Tag color="red">{data.high_risk_plugins.length}</Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        {data && data.high_risk_plugins.length > 0 ? (
          <Table
            columns={highRiskColumns}
            dataSource={data.high_risk_plugins.map(p => ({ ...p, key: p.plugin_id }))}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        ) : (
          <Alert
            message="No High-Risk Plugins"
            description="All enabled plugins have acceptable risk levels."
            type="success"
            showIcon
          />
        )}
      </Card>

      {/* Quarantined Plugins */}
      <Card
        title={
          <Space>
            <StopOutlined style={{ color: '#fa8c16' }} />
            <span>Quarantined Plugins</span>
            {data && (
              <Tag color="orange">{data.quarantined_plugins.length}</Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        {data && data.quarantined_plugins.length > 0 ? (
          <Table
            columns={quarantinedColumns}
            dataSource={data.quarantined_plugins.map(p => ({ ...p, key: p.plugin_id }))}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        ) : (
          <Alert
            message="No Quarantined Plugins"
            description="No plugins are currently quarantined."
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* Disabled Plugins */}
      <Card
        title={
          <Space>
            <PauseCircleOutlined />
            <span>Disabled Plugins</span>
            {data && (
              <Tag>{data.disabled_plugins.length}</Tag>
            )}
          </Space>
        }
        loading={loading}
      >
        {data && data.disabled_plugins.length > 0 ? (
          <Table
            columns={disabledColumns}
            dataSource={data.disabled_plugins.map(p => ({ ...p, key: p.plugin_id }))}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        ) : (
          <Alert
            message="No Disabled Plugins"
            description="All plugins are currently enabled."
            type="info"
            showIcon
          />
        )}
      </Card>

      {/* Detailed Plugin View */}
      <DetailedPluginView
        pluginId={selectedPluginId}
        visible={detailedViewVisible}
        onClose={() => {
          setDetailedViewVisible(false);
          setSelectedPluginId(null);
        }}
      />
    </div>
  );
};

export default SecurityReviewTab;

